"use client";

import Link from "next/link";
import base from "lib/base";
import {
  isSafeHref,
  isValidCdnUrl,
  escapeHtmlAttribute,
} from "lib/securityUtils";
import styles from "./HighlightsSection.module.css";

const buildCdnUrl = (pathname) => {
  if (!pathname) return "";

  // Validate base CDN URL
  if (!isValidCdnUrl(base.cdnUrl)) {
    console.error("Invalid CDN URL configuration");
    return "";
  }

  const normalizedBase = base.cdnUrl.endsWith("/")
    ? base.cdnUrl
    : `${base.cdnUrl}/`;
  const normalizedPath = String(pathname).replace(/^\/+/, "");

  try {
    const result = new URL(normalizedPath, normalizedBase).toString();
    // Ensure URL stays on approved CDN domain
    if (
      !result.startsWith(
        base.cdnUrl.split("://")[0] + "://" + new URL(base.cdnUrl).hostname,
      )
    ) {
      console.error("CDN URL escaped approved domain");
      return "";
    }
    return result;
  } catch (error) {
    console.error("Invalid CDN URL construction:", error);
    return "";
  }
};

export default function HighlightsSection({ highlights = [] }) {
  if (!highlights || highlights.length === 0) {
    return null;
  }

  return (
    <section
      className={styles.highlightsSection}
      id="highlights"
      aria-label="Featured Highlights"
      data-testid="highlights-section"
    >
      <div className="container">
        <div
          className={styles.highlightsContainer}
          role="region"
          aria-label="Featured promotional banners"
        >
          {highlights.map((highlight, index) => {
            // P0: Validate link safety
            const safeLink = isSafeHref(highlight.link) ? highlight.link : null;
            // P1: Escape alt text
            const safeAltText = escapeHtmlAttribute(
              highlight.name || "Featured Banner",
            );
            // P1: Escape title text
            const safeTitleText = escapeHtmlAttribute(highlight.name || "");

            const mobileBannerUrl = buildCdnUrl(highlight.mobileBanner);
            const tabletBannerUrl = buildCdnUrl(highlight.tabletBanner);
            const desktopBannerUrl = buildCdnUrl(highlight.desktopBanner);
            const desktopXlBannerUrl = buildCdnUrl(highlight.desktopXlBanner);

            // Skip if no valid banner URL
            if (!mobileBannerUrl) {
              console.warn(
                "Highlight missing valid banner URL:",
                highlight._id,
              );
              return null;
            }

            return (
              <article
                key={highlight._id || index}
                className={styles.bannerWrapper}
                data-testid={`highlight-${index}`}
                itemScope
                itemType="https://schema.org/CreativeWork"
              >
                <meta itemProp="name" content={safeTitleText} />
                <meta itemProp="url" content={safeLink || ""} />
                <meta itemProp="image" content={mobileBannerUrl} />
                {highlight.createdAt && (
                  <meta
                    itemProp="datePublished"
                    content={new Date(highlight.createdAt).toISOString()}
                  />
                )}
                {safeLink ? (
                  <Link
                    href={safeLink}
                    className={styles.bannerLink}
                    aria-label={`View ${safeAltText}`}
                  >
                    <picture className={styles.bannerPicture}>
                      {desktopXlBannerUrl && (
                        <>
                          <source
                            media="(min-width: 1320px)"
                            srcSet={desktopXlBannerUrl.replace(
                              /\.png$|$/,
                              ".webp",
                            )}
                            type="image/webp"
                          />
                          <source
                            media="(min-width: 1320px)"
                            srcSet={desktopXlBannerUrl}
                            type="image/png"
                          />
                        </>
                      )}
                      {desktopBannerUrl && (
                        <>
                          <source
                            media="(min-width: 1140px)"
                            srcSet={desktopBannerUrl.replace(
                              /\.png$|$/,
                              ".webp",
                            )}
                            type="image/webp"
                          />
                          <source
                            media="(min-width: 1140px)"
                            srcSet={desktopBannerUrl}
                            type="image/png"
                          />
                        </>
                      )}
                      {tabletBannerUrl && (
                        <>
                          <source
                            media="(min-width: 768px)"
                            srcSet={tabletBannerUrl.replace(
                              /\.png$|$/,
                              ".webp",
                            )}
                            type="image/webp"
                          />
                          <source
                            media="(min-width: 768px)"
                            srcSet={tabletBannerUrl}
                            type="image/png"
                          />
                        </>
                      )}
                      <img
                        src={mobileBannerUrl}
                        alt={safeAltText}
                        className={styles.bannerImage}
                        title={safeTitleText}
                        width={540}
                        height={200}
                        loading="lazy"
                        decoding="async"
                        itemProp="image"
                      />
                    </picture>
                  </Link>
                ) : (
                  <picture className={styles.bannerPicture}>
                    {desktopXlBannerUrl && (
                      <>
                        <source
                          media="(min-width: 1320px)"
                          srcSet={desktopXlBannerUrl.replace(
                            /\.png$|$/,
                            ".webp",
                          )}
                          type="image/webp"
                        />
                        <source
                          media="(min-width: 1320px)"
                          srcSet={desktopXlBannerUrl}
                          type="image/png"
                        />
                      </>
                    )}
                    {desktopBannerUrl && (
                      <>
                        <source
                          media="(min-width: 1140px)"
                          srcSet={desktopBannerUrl.replace(/\.png$|$/, ".webp")}
                          type="image/webp"
                        />
                        <source
                          media="(min-width: 1140px)"
                          srcSet={desktopBannerUrl}
                          type="image/png"
                        />
                      </>
                    )}
                    {tabletBannerUrl && (
                      <>
                        <source
                          media="(min-width: 768px)"
                          srcSet={tabletBannerUrl.replace(/\.png$|$/, ".webp")}
                          type="image/webp"
                        />
                        <source
                          media="(min-width: 768px)"
                          srcSet={tabletBannerUrl}
                          type="image/png"
                        />
                      </>
                    )}
                    <img
                      src={mobileBannerUrl}
                      alt={safeAltText}
                      className={styles.bannerImage}
                      title={safeTitleText}
                      width={540}
                      height={200}
                      loading="lazy"
                      decoding="async"
                      itemProp="image"
                    />
                  </picture>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
